// src/pages/ImportTeams.jsx
//
// Team-Import aus einem Google-Forms-Export.
//
// Ablauf (mit menschlichem Gate vor dem Schreiben):
//   1. CSV hochladen  → wird lokal geparst, Spalten werden erkannt.
//   2. Mapping         → jede Zielspalte der App wird einer CSV-Spalte zugeordnet
//                        (Heuristik schlaegt vor, Admin korrigiert).
//   3. Vorschau        → POST /teams/import { dryRun: true }: der Server prueft
//                        und meldet je Zeile anlegbar/Dublette/Fehler, schreibt
//                        aber NICHTS.
//   4. Importieren     → POST /teams/import { dryRun: false }: legt nur die
//                        anlegbaren Zeilen an, rechnet einmal neu.
//
// Die eigentliche Validierung/Normalisierung liegt bewusst im Backend
// (services/teamImport.js) – dieselben Regeln wie bei der Einzelanlage.

import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, SkipForward } from "lucide-react";
import Button from "../components/ui/Button";
import { parseCSV, leseDateiAlsText } from "../utils/parseTabular";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { notifyError, notifySuccess } from "../utils/notify";
import { kritischeAenderungBestaetigen } from "../utils/veranstaltungstag";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAPPING_STORAGE_KEY = "kg_import_mapping_v1";

// Zielfelder der App. Reihenfolge = Anzeigereihenfolge im Mapping.
const ZIELFELDER = [
  { key: "team_name", label: "Teamname", required: true },
  { key: "event_type", label: "Kategorie (Kinder/Erwachsene)", required: true },
  { key: "team_type", label: "Team-Typ (Männer/Frauen/Mixed)", required: true },
  { key: "contact_email", label: "Kontakt-E-Mail" },
  { key: "contact_phone", label: "Handynummer" },
  { key: "member1", label: "Mitglied 1" },
  { key: "member2", label: "Mitglied 2" },
  { key: "member3", label: "Mitglied 3" },
  { key: "member4", label: "Mitglied 4" },
  { key: "member5", label: "Mitglied 5" },
  { key: "member1_age", label: "Alter Mitglied 1 (nur Kinder)" },
  { key: "member2_age", label: "Alter Mitglied 2 (nur Kinder)" },
  { key: "member3_age", label: "Alter Mitglied 3 (nur Kinder)" },
  { key: "member4_age", label: "Alter Mitglied 4 (nur Kinder)" },
  { key: "member5_age", label: "Alter Mitglied 5 (nur Kinder)" },
];

const PFLICHT = ZIELFELDER.filter((f) => f.required).map((f) => f.key);

/** Spaltenkopf fuer den Heuristik-Vergleich normalisieren. */
function normHeader(h) {
  return String(h || "")
    .toLowerCase()
    .replace(/ä/g, "a").replace(/ö/g, "o").replace(/ü/g, "u").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Spalten automatisch auf Zielfelder vorschlagen. Reserviert bereits zugeordnete
 * Spalten (eine Spalte kann nur ein Ziel treffen). Reihenfolge von speziell nach
 * allgemein, damit z. B. „Team-Typ" nicht faelschlich als Teamname landet.
 */
function autoMap(headers) {
  const norm = headers.map((h) => ({ h, n: normHeader(h) }));
  const belegt = new Set();
  const map = {};

  const finde = (praedikat) => {
    const treffer = norm.find(({ h, n }) => !belegt.has(h) && praedikat(n));
    if (treffer) { belegt.add(treffer.h); return treffer.h; }
    return "";
  };

  for (let i = 1; i <= 5; i++) {
    map[`member${i}_age`] = finde((n) => /(alter|age|jahre)/.test(n) && n.includes(String(i)));
  }
  for (let i = 1; i <= 5; i++) {
    map[`member${i}`] = finde(
      (n) => /(mitglied|member|spieler|teilnehmer|name)/.test(n) && n.includes(String(i))
    );
  }
  map.contact_email = finde((n) => n.includes("mail"));
  // Vor team_name suchen, sonst schnappt sich dessen "nummer"-freie Heuristik
  // nichts – aber "telefonnummer" darf nicht als Teamname enden.
  map.contact_phone = finde((n) => /(handy|telefon|phone|mobil|tel)/.test(n));
  map.event_type = finde((n) => /(kategorie|kinder|erwachsene|altersklasse|event)/.test(n));
  map.team_type = finde((n) => /(typ|art|geschlecht|manner|maenner|frauen|mixed)/.test(n));
  map.team_name = finde((n) => /(team|mannschaft|gruppe|name)/.test(n));

  // Fehlende Ziele mit "" auffuellen
  for (const f of ZIELFELDER) if (!(f.key in map)) map[f.key] = "";
  return map;
}

/** Stabiler Fingerabdruck der Kopfzeile (fuer gemerktes Mapping). */
function headerSignatur(headers) {
  return headers.map((h) => h.trim()).join("|");
}

function ladeGemerktesMapping(headers) {
  try {
    const roh = JSON.parse(localStorage.getItem(MAPPING_STORAGE_KEY) || "{}");
    return roh[headerSignatur(headers)] || null;
  } catch {
    return null;
  }
}

function merkeMapping(headers, mapping) {
  try {
    const roh = JSON.parse(localStorage.getItem(MAPPING_STORAGE_KEY) || "{}");
    roh[headerSignatur(headers)] = mapping;
    localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(roh));
  } catch {
    // localStorage kann fehlschlagen (privater Modus) – nicht kritisch.
  }
}

const STATUS_META = {
  created: { text: "angelegt", cls: "text-green-400", Icon: CheckCircle2 },
  create: { text: "wird angelegt", cls: "text-green-400", Icon: CheckCircle2 },
  skipped: { text: "übersprungen", cls: "text-yellow-400", Icon: SkipForward },
  error: { text: "Fehler", cls: "text-red-400", Icon: AlertTriangle },
};

export default function ImportTeams() {
  const navigate = useNavigate();
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [report, setReport] = useState(null); // { dryRun, summary, rows }
  const [busy, setBusy] = useState(false);

  const fehlendePflicht = useMemo(
    () => PFLICHT.filter((k) => !mapping[k]),
    [mapping]
  );

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    try {
      const text = await leseDateiAlsText(file);
      const { headers, rows } = parseCSV(text);
      if (headers.length === 0 || rows.length === 0) {
        notifyError("Keine Datenzeilen in der Datei gefunden.");
        return;
      }
      const gemerkt = ladeGemerktesMapping(headers);
      setFileName(file.name);
      setHeaders(headers);
      setRows(rows);
      setMapping(gemerkt || autoMap(headers));
      setReport(null);
      notifySuccess(`${rows.length} Zeile(n) eingelesen.`);
    } catch (err) {
      console.error("[ImportTeams] Datei-Fehler:", err);
      notifyError("Datei konnte nicht gelesen werden.");
    }
  }, []);

  // Gemappte Zeilen fuer den Server bauen – nur zugeordnete Zielfelder.
  const baueZeilen = useCallback(() => {
    return rows.map((row) => {
      const obj = {};
      for (const f of ZIELFELDER) {
        const spalte = mapping[f.key];
        if (spalte && Object.prototype.hasOwnProperty.call(row, spalte)) {
          obj[f.key] = row[spalte];
        }
      }
      return obj;
    });
  }, [rows, mapping]);

  const sende = useCallback(
    async (dryRun) => {
      if (fehlendePflicht.length > 0) {
        notifyError("Bitte zuerst alle Pflichtspalten zuordnen.");
        return;
      }
      // Nur der echte Import ist kritisch, die Vorschau aendert nichts.
      if (!dryRun && !(await kritischeAenderungBestaetigen("Teams importieren."))) return;
      setBusy(true);
      try {
        merkeMapping(headers, mapping);
        const data = await fetchWithAuth(`${BACKEND_URL}/teams/import`, navigate, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ teams: baueZeilen(), dryRun }),
        });
        setReport(data);
        if (dryRun) {
          notifySuccess(`Vorschau: ${data.summary.anlegbar} anlegbar, ${data.summary.dubletten} Dublette(n), ${data.summary.fehler} Fehler.`);
        } else {
          notifySuccess(`${data.created} Team(s) importiert.`);
        }
      } catch (err) {
        console.error("[ImportTeams] Import-Fehler:", err);
        notifyError("Import fehlgeschlagen: " + (err.message || "Unbekannter Fehler"));
      } finally {
        setBusy(false);
      }
    },
    [navigate, headers, mapping, baueZeilen, fehlendePflicht]
  );

  const spaltenOptionen = ["", ...headers];
  const importiert = report && !report.dryRun;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Teams importieren</h1>
        <p className="text-gray-400 mt-1 text-sm">
          Google-Forms-Antworten als CSV exportieren (im Formular-Sheet: <em>Datei → Herunterladen → CSV</em>),
          hier hochladen, Spalten zuordnen und vor dem Import in der Vorschau prüfen.
        </p>
      </div>

      {/* Upload */}
      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-secondary-700 rounded-xl p-8 cursor-pointer hover:border-primary-500 transition-colors bg-secondary-900">
        <UploadCloud className="text-primary-500" size={32} />
        <span className="text-white font-medium">CSV-Datei auswählen</span>
        <span className="text-gray-400 text-sm">{fileName || "Noch keine Datei gewählt"}</span>
        <input
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>

      {/* Mapping */}
      {headers.length > 0 && (
        <div className="bg-secondary-900 border border-secondary-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet size={18} className="text-primary-500" />
            <h2 className="text-lg font-semibold text-white">Spalten zuordnen</h2>
            <span className="text-gray-400 text-sm">({rows.length} Zeilen)</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ZIELFELDER.map((f) => {
              const fehlt = f.required && !mapping[f.key];
              return (
                <div key={f.key} className="flex flex-col space-y-1">
                  <label className="text-sm text-gray-300">
                    {f.label} {f.required && <span className="text-red-400">*</span>}
                  </label>
                  <select
                    value={mapping[f.key] || ""}
                    onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                    className={`w-full bg-secondary-800 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      fehlt ? "border-red-500" : "border-secondary-700"
                    }`}
                  >
                    {spaltenOptionen.map((opt) => (
                      <option key={opt || "__none"} value={opt} className="bg-secondary-900">
                        {opt === "" ? "— nicht zuordnen —" : opt}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          {fehlendePflicht.length > 0 && (
            <p className="text-yellow-400 text-sm mt-4 flex items-center gap-2">
              <AlertTriangle size={16} />
              Pflichtspalten fehlen: {fehlendePflicht.join(", ")}
            </p>
          )}

          <div className="flex flex-wrap gap-3 mt-5">
            <Button variant="secondary" onClick={() => sende(true)} disabled={busy || fehlendePflicht.length > 0}>
              {busy ? "Prüfe…" : "Vorschau erstellen"}
            </Button>
            <Button
              onClick={() => sende(false)}
              disabled={busy || !report || report.dryRun === false || report.summary.anlegbar === 0}
              title={!report ? "Zuerst Vorschau erstellen" : ""}
            >
              {busy ? "Importiere…" : report && report.dryRun ? `${report.summary.anlegbar} Team(s) importieren` : "Importieren"}
            </Button>
          </div>
        </div>
      )}

      {/* Bericht */}
      {report && (
        <div className="bg-secondary-900 border border-secondary-800 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white mb-1">
            {importiert ? "Import abgeschlossen" : "Vorschau"}
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            {importiert
              ? `${report.created} angelegt · `
              : `${report.summary.anlegbar} anlegbar · `}
            {report.summary.dubletten} Dublette(n) · {report.summary.fehler} Fehler · {report.summary.gesamt} gesamt
          </p>

          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto rounded-lg border border-secondary-800">
            <table className="w-full text-sm">
              <thead className="bg-secondary-800 text-gray-300 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Zeile</th>
                  <th className="text-left px-3 py-2 font-medium">Teamname</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Hinweis</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.error;
                  const Icon = meta.Icon;
                  return (
                    <tr key={r.index} className="border-t border-secondary-800">
                      {/* +2: Kopfzeile (1) + 0-basierter Index → Zeilennummer im Sheet */}
                      <td className="px-3 py-2 text-gray-400">{r.index + 2}</td>
                      <td className="px-3 py-2 text-white">{r.team_name || <span className="text-gray-500">—</span>}</td>
                      <td className={`px-3 py-2 ${meta.cls}`}>
                        <span className="inline-flex items-center gap-1">
                          <Icon size={14} /> {meta.text}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-400">{r.reason || ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {importiert && (
            <div className="mt-4">
              <Button variant="secondary" onClick={() => navigate("/teams")}>Zu den Teams</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
